import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  orderBy,
  query,
} from 'firebase/firestore';
import { EmptyState } from '../components/EmptyState';
import { ReadFailure } from '../components/ReadFailure';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field, Select, TextInput } from '../components/ui/Field';
import { Switch } from '../components/ui/Switch';
import { useConfirm } from '../components/ui/Confirm';
import { Search } from 'lucide-react';
import { listenQuery } from '../lib/liveRead';
import { useAuth } from '../auth/AuthProvider';
import { db, functions } from '../lib/firebase';
import { formatTimestamp } from '../lib/format';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  ALL_MEMBER_ROLES,
  ROLE_LABELS,
  type CompanyMember,
  type MemberRole,
  type SubcontractorRecord,
} from '../types/models';

type UserForm = {
  email: string;
  displayName: string;
  role: MemberRole;
  subcontractorId: string;
  isActive: boolean;
};

type SaveMemberResponse = {
  success: boolean;
  authUserCreated: boolean;
  memberAction: 'updated' | 'reactivated' | 'invited' | string;
  userId?: string;
  // Present only when memberAction === 'invited' (P0-01/N3): no company
  // membership exists yet, so there's no userId — these three build the
  // acceptance link instead. Not emailed automatically yet
  // (needs one.com SMTP, tracked separately), so the admin gets it here to
  // send however they like meanwhile.
  invitationId?: string;
  companyId?: string;
  token?: string;
};

const EMPTY_FORM: UserForm = {
  email: '',
  displayName: '',
  role: 'driver',
  subcontractorId: '',
  isActive: true,
};

// What listCompanyInvitations returns: a pending invitation without its token.
// Invitations are server-only (rules deny every client read), so this list is
// the only way an admin sees one between sending it and its acceptance.
type PendingInvitation = {
  invitationId: string;
  email: string;
  displayName: string;
  role: MemberRole | null;
  subcontractorId: string | null;
  invitedBy: string | null;
  sentAt: string | null;
  expiresAt: string | null;
  expired: boolean;
};

function formatIsoDate(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export default function UsersPage() {
  usePageTitle('Users and roles');
  const { activeCompanyId, activeRole, user } = useAuth();
  const confirm = useConfirm();
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorRecord[]>(
    []
  );
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CompanyMember | null>(null);
  const [form, setForm] = useState<UserForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [subcontractorsLoading, setSubcontractorsLoading] = useState(true);
  const [subcontractorsError, setSubcontractorsError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The Add/Edit panel shows only once Add user or Edit is pressed: always on
  // screen, it read as a form waiting to be filled in (Bobby, 9 Oct 2026).
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const [invitationsLoaded, setInvitationsLoaded] = useState(false);
  const [invitationsAttempt, setInvitationsAttempt] = useState(0);
  const [invitationBusyId, setInvitationBusyId] = useState<string | null>(null);

  const availableRoles = useMemo(() => {
    if (activeRole === 'superadmin') return ALL_MEMBER_ROLES;
    return ALL_MEMBER_ROLES.filter((role) => role !== 'superadmin');
  }, [activeRole]);

  useEffect(() => {
    if (!activeCompanyId) {
      setMembers([]);
      setSubcontractors([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setSubcontractorsLoading(true);
    setSubcontractorsError(null);
    setReadError(null);
    setMembers([]);
    setSubcontractors([]);
    setEditing(null);
    setForm(EMPTY_FORM);
    setPanelOpen(false);
    setError(null);
    const membersRef = collection(db, 'companies', activeCompanyId, 'members');
    const subcontractorsRef = collection(
      db,
      'companies',
      activeCompanyId,
      'subcontractors'
    );

    const unsubMembers = listenQuery(
      query(membersRef, orderBy('displayName')),
      (snap) => {
        setMembers(
          snap.docs.map((memberDoc) => {
            const data = memberDoc.data() as Omit<CompanyMember, 'id'>;
            return {
              id: memberDoc.id,
              ...data,
              userId: data.userId || memberDoc.id,
              companyId: activeCompanyId,
            } as CompanyMember;
          })
        );
        setReadError(null);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setReadError(err.message);
        setLoading(false);
      }
    );

    const unsubSubcontractors = listenQuery(
      query(subcontractorsRef, orderBy('name')),
      (snap) => {
        setSubcontractors(
          snap.docs
            .map((subcontractorDoc) => ({
              id: subcontractorDoc.id,
              ...(subcontractorDoc.data() as Omit<SubcontractorRecord, 'id'>),
            }))
            .filter((subcontractor) => subcontractor.isActive !== false)
        );
        setSubcontractorsLoading(false);
        setSubcontractorsError(null);
      },
      (err) => {
        console.warn('Failed to load subcontractors for user editor', err);
        setSubcontractorsLoading(false);
        setSubcontractorsError(err.message);
      }
    );

    return () => {
      unsubMembers();
      unsubSubcontractors();
    };
  }, [activeCompanyId, readAttempt]);

  useEffect(() => {
    if (!activeCompanyId) {
      setInvitations([]);
      setInvitationsLoaded(false);
      return;
    }
    let cancelled = false;
    setInvitationsError(null);
    const listInvitations = httpsCallable<{ companyId: string }, { invitations: PendingInvitation[] }>(
      functions,
      'listCompanyInvitations'
    );
    listInvitations({ companyId: activeCompanyId })
      .then((result) => {
        if (cancelled) return;
        setInvitations(result.data.invitations ?? []);
        setInvitationsLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setInvitations([]);
        setInvitationsLoaded(true);
        setInvitationsError((e as Error).message || 'Could not load pending invitations.');
      });
    return () => {
      cancelled = true;
    };
  }, [activeCompanyId, invitationsAttempt]);

  useEffect(() => {
    // On a narrow screen the panel sits below the list, so bring it into view.
    if (panelOpen) panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [panelOpen, editing]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((member) =>
      [
        member.displayName,
        member.email,
        member.userId,
        ROLE_LABELS[member.role],
        member.subcontractorId,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [members, search]);

  function startNew() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setNotice(null);
    setPanelOpen(true);
  }

  function startEdit(member: CompanyMember) {
    setEditing(member);
    setForm({
      email: member.email ?? '',
      displayName: member.displayName ?? '',
      role: member.role,
      subcontractorId: member.subcontractorId ?? '',
      isActive: member.isActive !== false,
    });
    setNotice(null);
    setPanelOpen(true);
  }

  function closePanel() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setPanelOpen(false);
  }

  // The link carries the one-time token, which the server returns only now: it
  // can't be shown again later, only replaced by a resend.
  async function handOverInvitationLink(response: SaveMemberResponse, email: string) {
    const acceptUrl = `${window.location.origin}/accept-invitation?company=${encodeURIComponent(response.companyId ?? activeCompanyId ?? '')}&invitation=${encodeURIComponent(response.invitationId ?? '')}&token=${encodeURIComponent(response.token ?? '')}`;
    try {
      await navigator.clipboard.writeText(acceptUrl);
      setNotice(`Invitation link copied to your clipboard — send it to ${email}. It expires in 7 days.`);
    } catch {
      setNotice(`Invitation created for ${email}. Copy this link and send it to them (expires in 7 days): ${acceptUrl}`);
    }
  }

  async function resendInvitation(invitation: PendingInvitation) {
    if (!activeCompanyId || !invitation.role) return;
    setInvitationBusyId(invitation.invitationId);
    setError(null);
    setNotice(null);
    try {
      // Inviting the same address again reuses its pending invitation with a
      // fresh link and a fresh 7 days; the old link stops working.
      const saveMember = httpsCallable<
        { companyId: string; email: string; displayName?: string; role: MemberRole; subcontractorId?: string | null; isActive: boolean },
        SaveMemberResponse
      >(functions, 'saveCompanyMember');
      const response = await saveMember({
        companyId: activeCompanyId,
        email: invitation.email,
        displayName: invitation.displayName || undefined,
        role: invitation.role,
        subcontractorId: invitation.role === 'subcontractor' ? invitation.subcontractorId : null,
        isActive: true,
      });
      if (response.data.memberAction === 'invited' && response.data.token) {
        await handOverInvitationLink(response.data, invitation.email);
      } else {
        setNotice('User updated.');
      }
      setInvitationsAttempt((n) => n + 1);
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not resend the invitation.');
    } finally {
      setInvitationBusyId(null);
    }
  }

  async function revokeInvitation(invitation: PendingInvitation) {
    if (!activeCompanyId) return;
    const ok = await confirm({
      title: 'Revoke this invitation?',
      message: `${invitation.email} can no longer use the link to join this company. You can invite them again later.`,
      confirmLabel: 'Revoke',
      tone: 'danger',
    });
    if (!ok) return;

    setInvitationBusyId(invitation.invitationId);
    setError(null);
    setNotice(null);
    try {
      const revoke = httpsCallable<{ companyId: string; invitationId: string }, { success: boolean }>(
        functions,
        'revokeCompanyInvitation'
      );
      await revoke({ companyId: activeCompanyId, invitationId: invitation.invitationId });
      setNotice(`Invitation for ${invitation.email} revoked.`);
      setInvitationsAttempt((n) => n + 1);
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not revoke the invitation.');
      setInvitationsAttempt((n) => n + 1);
    } finally {
      setInvitationBusyId(null);
    }
  }

  function setField<K extends keyof UserForm>(key: K, value: UserForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeCompanyId) return;
    if (form.role === 'subcontractor' && (subcontractorsLoading || subcontractorsError || !form.subcontractorId)) {
      setError('Choose a subcontractor record for subcontractor users.');
      return;
    }
    if (!editing && !form.email.trim()) {
      setError('Email is required for new users.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveMember = httpsCallable<
        {
          companyId: string;
          userId?: string;
          email?: string;
          displayName?: string;
          role: MemberRole;
          subcontractorId?: string | null;
          isActive: boolean;
        },
        SaveMemberResponse
      >(functions, 'saveCompanyMember');

      const response = await saveMember({
        companyId: activeCompanyId,
        userId: editing?.userId,
        email: editing ? undefined : form.email.trim(),
        displayName: form.displayName.trim() || undefined,
        role: form.role,
        subcontractorId:
          form.role === 'subcontractor' ? form.subcontractorId : null,
        isActive: form.isActive,
      });

      if (response.data.memberAction === 'invited' && response.data.token) {
        // P0-01/N3: nobody joins until they accept, and accepting needs more than
        // this link: they must also sign in with a link Firebase emails to the
        // address, which only its owner can open. So handing it over is safe.
        await handOverInvitationLink(response.data, form.email.trim());
        setInvitationsAttempt((n) => n + 1);
      } else if (response.data.memberAction === 'reactivated') {
        setNotice('User reactivated.');
      } else {
        setNotice(editing ? 'User updated.' : 'User added.');
      }

      closePanel();
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not save user.');
    } finally {
      setSaving(false);
    }
  }

  async function quickUpdate(member: CompanyMember, changes: Partial<UserForm>) {
    if (!activeCompanyId) return;
    setBusyId(member.userId);
    setError(null);
    setNotice(null);
    try {
      const nextRole = changes.role ?? member.role;
      const nextSubcontractorId =
        nextRole === 'subcontractor'
          ? changes.subcontractorId ?? member.subcontractorId ?? ''
          : null;
      if (nextRole === 'subcontractor' && !nextSubcontractorId) {
        throw new Error('Choose a subcontractor record before assigning this role.');
      }

      const saveMember = httpsCallable<
        {
          companyId: string;
          userId: string;
          displayName?: string;
          role: MemberRole;
          subcontractorId?: string | null;
          isActive: boolean;
        },
        SaveMemberResponse
      >(functions, 'saveCompanyMember');

      await saveMember({
        companyId: activeCompanyId,
        userId: member.userId,
        displayName: changes.displayName ?? member.displayName ?? undefined,
        role: nextRole,
        subcontractorId: nextSubcontractorId,
        isActive: changes.isActive ?? member.isActive !== false,
      });
      setNotice('User updated.');
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not update user.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(member: CompanyMember) {
    if (!activeCompanyId || activeRole !== 'superadmin') return;
    const who = member.displayName || member.email || member.userId;
    const ok = await confirm({
      title: 'Remove from this company?',
      message: `${who} loses access to this company's jobs and evidence. Their account and anything they have already filed stay as they are, and they can be added again later.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;

    setBusyId(member.userId);
    setError(null);
    setNotice(null);
    try {
      const removeMember = httpsCallable<
        { companyId: string; userId: string },
        { success: boolean }
      >(functions, 'removeCompanyMember');
      await removeMember({ companyId: activeCompanyId, userId: member.userId });
      setNotice('User removed from this company.');
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not remove user.');
    } finally {
      setBusyId(null);
    }
  }

  if (!activeCompanyId) {
    return (
      <EmptyState
        title="No company selected"
        description="Choose a company before managing users."
      />
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">Users and roles</h1>
          <p className="mt-1.5 text-[15px] text-ink2">Who can see and do what in this company.</p>
        </div>
        <Button type="button" onClick={startNew}>Add user</Button>
      </div>

      <div className={`mt-5 grid items-start gap-6 ${panelOpen ? 'xl:grid-cols-[minmax(0,1fr)_380px]' : ''}`}>
        <div>
          <div className="flex h-11 items-center gap-2.5 rounded-[10px] bg-search px-3.5 text-ink3 focus-within:shadow-[inset_0_0_0_1.5px_var(--accent),0_0_0_3px_var(--accent-ring)]">
            <Search size={18} strokeWidth={1.8} aria-hidden />
            <input
              type="search"
              aria-label="Search users"
              placeholder="Search name, email, role or user id"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink3"
            />
          </div>

          <div className="mt-3 space-y-2.5 empty:hidden">
            {error && <Alert tone="error">{error}</Alert>}
            {notice && <Alert tone="success">{notice}</Alert>}
            {readError && <ReadFailure message={readError} retry={() => setReadAttempt((n) => n + 1)} />}
          </div>

          <div className="mt-3.5">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-[14px] border border-hair bg-elev px-4 py-3.5">
                    <div className="skeleton h-4 w-36 rounded-md" />
                    <div className="skeleton mt-2 h-3.5 w-52 rounded-md" />
                  </div>
                ))}
              </div>
            ) : readError ? null : filteredMembers.length === 0 ? (
              <EmptyState
                title="No users"
                description="Add users by email. An existing Checkover account is linked to this company rather than duplicated."
              />
            ) : (
              <div className="flex flex-col gap-2">
                {filteredMembers.map((member) => {
                  const isSelf = user?.uid === member.userId;
                  const active = member.isActive !== false;
                  const busy = busyId === member.userId;
                  return (
                    /* A deactivated member stays in the list, dimmed: removing the row
                       is how you lose track of who used to have access. */
                    <div
                      key={member.userId}
                      className={`rounded-[14px] border border-hair bg-elev px-4 py-3.5 ${active ? '' : 'opacity-60'}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button type="button" onClick={() => startEdit(member)}
                            className="text-left text-[16px] font-semibold text-ink hover:text-accent">
                            {member.displayName || member.email || 'Unnamed user'}
                          </button>
                          <div className="mt-0.5 truncate font-mono text-[12.5px] text-ink3">
                            {member.email || 'No email'}
                          </div>
                          {member.role === 'subcontractor' && (
                            <div className="mt-1 text-[13px] text-ink2">
                              {subcontractorName(subcontractors, member.subcontractorId)}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {/* Role is not a state, so it is not coloured like one. */}
                          {isSelf ? (
                            <>
                              <span className={`job-tag role-pill ${member.role === 'accounts' ? 'role-pill-accounts' : ''}`}>{ROLE_LABELS[member.role]}</span>
                              <span className="eyebrow">You</span>
                            </>
                          ) : (
                            <Select
                              aria-label={`Role for ${member.displayName || member.email || member.userId}`}
                              value={member.role}
                              disabled={busy}
                              onChange={(event) => quickUpdate(member, { role: event.target.value as MemberRole })}
                              className={`role-pill h-9 w-[150px] text-[14px] ${member.role === 'accounts' ? 'role-pill-accounts' : ''}`}
                            >
                              {roleOptionsForMember(member, availableRoles).map((role) => (
                                <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                              ))}
                            </Select>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2.5">
                        {isSelf ? (
                          <span className="text-[13px] text-ink3">{active ? 'Active' : 'Not active'}</span>
                        ) : (
                          <span className="flex items-center gap-2.5">
                            <Switch
                              label={`Active in this company: ${member.displayName || member.email || member.userId}`}
                              checked={active}
                              disabled={busy}
                              onChange={(checked) => quickUpdate(member, { isActive: checked })}
                            />
                            <span className="text-[13.5px] text-ink2">{active ? 'Active' : 'Deactivated'}</span>
                          </span>
                        )}
                        {member.updatedAt && (
                          <span className="text-[12.5px] text-ink3">
                            Updated <span className="font-mono">{formatTimestamp(member.updatedAt)}</span>
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-2">
                          <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(member)}>Edit</Button>
                          {!isSelf && activeRole === 'superadmin' && (
                            <Button type="button" variant="danger-outline" size="sm" disabled={busy} onClick={() => remove(member)}>
                              Remove
                            </Button>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {invitationsLoaded && (invitations.length > 0 || invitationsError) && (
            <section className="mt-8" aria-labelledby="pending-invitations-heading">
              <h2 id="pending-invitations-heading" className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">
                Pending invitations{invitations.length > 0 ? ` (${invitations.length})` : ''}
              </h2>
              <p className="mt-1 text-[13.5px] text-ink3">
                Sent but not accepted yet. They join only once they accept. Resend makes a fresh link for
                another 7 days and the old one stops working; Revoke withdraws the invitation.
              </p>
              {invitationsError ? (
                <div className="mt-3">
                  <ReadFailure message={invitationsError} retry={() => setInvitationsAttempt((n) => n + 1)} />
                </div>
              ) : (
                <div className="mt-3 flex flex-col gap-2">
                  {invitations.map((invitation) => {
                    const busy = invitationBusyId === invitation.invitationId;
                    const inviter = members.find((member) => member.userId === invitation.invitedBy);
                    return (
                      <div key={invitation.invitationId} className="rounded-[14px] border border-hair bg-elev px-4 py-3.5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[16px] font-semibold text-ink">
                              {invitation.displayName || invitation.email}
                            </div>
                            <div className="mt-0.5 truncate font-mono text-[12.5px] text-ink3">{invitation.email}</div>
                          </div>
                          <span className="state-badge state-pending is-sm">
                            {invitation.role ? ROLE_LABELS[invitation.role] : 'No role'}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2.5">
                          <span className="text-[12.5px] text-ink3">
                            Sent <span className="font-mono">{formatIsoDate(invitation.sentAt)}</span>
                            {inviter ? ` by ${inviter.displayName || inviter.email}` : ''}
                          </span>
                          <span className={`text-[12.5px] ${invitation.expired ? 'font-semibold text-outstanding-ink' : 'text-ink3'}`}>
                            {invitation.expired ? 'Expired' : 'Expires'}{' '}
                            <span className="font-mono">{formatIsoDate(invitation.expiresAt)}</span>
                          </span>
                          <span className="ml-auto flex items-center gap-2">
                            <Button type="button" variant="secondary" size="sm" disabled={busy || !invitation.role}
                              onClick={() => resendInvitation(invitation)}>
                              Resend link
                            </Button>
                            <Button type="button" variant="danger-outline" size="sm" disabled={busy}
                              onClick={() => revokeInvitation(invitation)}>
                              Revoke
                            </Button>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>

        {panelOpen && (
        <section ref={panelRef} className="overflow-hidden rounded-[14px] border border-hair bg-elev xl:sticky xl:top-2">
          <div className="px-5 pt-4">
            <h2 className="text-[16.5px] font-semibold tracking-[-0.01em] text-ink">{editing ? 'Edit user' : 'Add user'}</h2>
            <p className="mt-1 text-[13.5px] text-ink3">
              New people get an invitation link to accept themselves: they confirm their email address
              and choose their own password. Nobody joins a company by email alone.
            </p>
          </div>

          <form onSubmit={save} className="flex flex-col gap-4 p-5">
            <Field label="Email">
              <TextInput type="email" value={form.email} required={!editing} disabled={!!editing}
                onChange={(event) => setField('email', event.target.value)} placeholder="name@company.co.uk" />
            </Field>
            <Field label="Display name">
              <TextInput value={form.displayName} onChange={(event) => setField('displayName', event.target.value)}
                placeholder="Their name" />
            </Field>
            <Field label="Role">
              <Select value={form.role} onChange={(event) => setField('role', event.target.value as MemberRole)}>
                {roleOptionsForMember(editing, availableRoles).map((role) => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </Select>
            </Field>

            {form.role === 'subcontractor' && (
              <Field label="Subcontractor">
                <Select value={form.subcontractorId} disabled={subcontractorsLoading || !!subcontractorsError}
                  onChange={(event) => setField('subcontractorId', event.target.value)}>
                  <option value="">{subcontractorsLoading ? 'Loading…' : 'Choose subcontractor'}</option>
                  {subcontractors.map((subcontractor) => (
                    <option key={subcontractor.id} value={subcontractor.id}>
                      {subcontractor.shortName || subcontractor.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {form.role === 'subcontractor' && subcontractorsError && (
              <ReadFailure message={subcontractorsError} retry={() => setReadAttempt((n) => n + 1)} />
            )}

            <div className="flex items-center justify-between gap-4 rounded-[10px] bg-surface px-3.5 py-3">
              <span>
                <span className="block text-[15px] font-semibold text-ink">Active in this company</span>
                <span className="mt-0.5 block text-[13px] text-ink3">Turn off to keep the record without access.</span>
              </span>
              <Switch label="Active in this company" checked={form.isActive}
                onChange={(checked) => setField('isActive', checked)} />
            </div>

            <div className="flex gap-2">
              <Button type="submit" loading={saving}
                disabled={
                  saving ||
                  (!editing && !form.email.trim()) ||
                  (form.role === 'subcontractor' && (subcontractorsLoading || !!subcontractorsError || !form.subcontractorId))
                }>
                {editing ? 'Save changes' : 'Add user'}
              </Button>
              <Button type="button" variant="secondary" onClick={closePanel}>Cancel</Button>
            </div>
          </form>
        </section>
        )}
      </div>
    </div>
  );
}

function roleOptionsForMember(
  member: CompanyMember | null,
  availableRoles: MemberRole[]
) {
  if (member?.role && !availableRoles.includes(member.role)) {
    return [member.role, ...availableRoles];
  }
  return availableRoles;
}

function subcontractorName(
  subcontractors: SubcontractorRecord[],
  subcontractorId?: string | null
) {
  if (!subcontractorId) return 'No subcontractor selected';
  const subcontractor = subcontractors.find((item) => item.id === subcontractorId);
  return subcontractor?.shortName || subcontractor?.name || subcontractorId;
}
